"use client";

import { useAppStore, type ViewName } from "@/lib/store";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/booking/theme-toggle";
import { LanguageSwitcher } from "@/components/booking/language-switcher";
import { isStaffRole, roleBadgeClass, roleLabel  } from "@/components/booking/format";
import {
  Home,
  CalendarCheck,
  Building2,
  Shield,
  ShieldCheck,
  Receipt,
  PieChart,
  Bell,
  LogOut,
  Menu,
  UserCircle,
  LayoutDashboard,
  Eye,
  BookOpen,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { apiPost, apiDelete, apiGet } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { PreviewUsersModal } from "@/components/booking/preview-users-modal";

function NavButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={`gap-2 border-0 ${
        active
          ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
          : "text-foreground hover:bg-muted"
      }`}
    >
      <Icon className="h-4 w-4" />
      {children}
    </Button>
  );
}

export function Header() {
  const {
    user,
    view,
    goBrowse: goBrowseStore,
    setView: setViewStore,
    openAuth,
    setUser,
    openNewProduct,
    impersonating,
    setImpersonating,
  } = useAppStore();
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  // The header is shared by the SPA ("/") and the /documentation route, so
  // view switches must also leave that route when one is open.
  const setView = (v: ViewName) => {
    if (pathname !== "/") router.push("/");
    setViewStore(v);
  };
  const goBrowse = () => {
    if (pathname !== "/") router.push("/");
    goBrowseStore();
  };
  const docsActive = pathname?.startsWith("/documentation") ?? false;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const logout = async () => {
    try {
      await apiPost("/api/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    setImpersonating(false);
    qc.clear();
    goBrowse();
    toast.success(t("toast.signedOut"));
  };

  const exitPreview = async () => {
    try {
      await apiDelete("/api/auth/impersonate");
    } catch {
      /* ignore */
    }
    qc.clear();
    try {
      const res = await apiGet<{ user: any; impersonating?: boolean }>(
        "/api/auth/me"
      );
      setUser(res.user ?? null);
      setImpersonating(!!res.impersonating);
    } catch {
      setUser(null);
      setImpersonating(false);
    }
    goBrowse();
    toast.success(t("preview.exited"));
  };

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username
    : "";

  const nav = (
    <>
      <NavButton active={view === "browse" && !docsActive} onClick={goBrowse} icon={Home}>
        {t("nav.browse")}
      </NavButton>
      {user && (
        <NavButton
          active={view === "my-reservations"}
          onClick={() => setView("my-reservations")}
          icon={CalendarCheck}
        >
          {t("nav.myTrips")}
        </NavButton>
      )}
      {user && (user.role === "OWNER" || user.role === "ADMIN") && (
        <NavButton
          active={view === "host"}
          onClick={() => setView("host")}
          icon={Building2}
        >
          {t("nav.hostPanel")}
        </NavButton>
      )}
      {user && (isStaffRole(user.role)) && (
        <>
          <NavButton
            active={view === "moderator"}
            onClick={() => setView("moderator")}
            icon={ShieldCheck}
          >
            {t("nav.moderator")}
          </NavButton>
          <NavButton
            active={view === "expenses"}
            onClick={() => setView("expenses")}
            icon={Receipt}
          >
            {t("nav.expenses")}
          </NavButton>
          <NavButton
            active={view === "dashboard"}
            onClick={() => setView("dashboard")}
            icon={PieChart}
          >
            {t("nav.dashboard")}
          </NavButton>
          <NavButton
            active={view === "notifications"}
            onClick={() => setView("notifications")}
            icon={Bell}
          >
            {t("nav.notifications")}
          </NavButton>
        </>
      )}
      {user && user.role === "ADMIN" && (
        <NavButton
          active={view === "admin"}
          onClick={() => setView("admin")}
          icon={Shield}
        >
          {t("nav.admin")}
        </NavButton>
      )}
      <Button
        variant="ghost"
        size="sm"
        className={`gap-2 border-0 ${
          docsActive
            ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
            : "text-foreground hover:bg-muted"
        }`}
        asChild
      >
        <Link href="/documentation">
          <BookOpen className="h-4 w-4" />
          {t("nav.documentation")}
        </Link>
      </Button>
    </>
  );

  return (
    <>
    <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {impersonating && user && (
        <div className="border-b border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-200">
          <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {t("preview.banner", {
                  name: displayName || user.username,
                  role: roleLabel(user.role, locale),
                })}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 shrink-0 gap-1.5 border-amber-300 bg-transparent px-2.5 hover:bg-amber-200/60 dark:border-amber-800 dark:hover:bg-amber-900/40"
              onClick={exitPreview}
            >
              <LogOut className="h-3.5 w-3.5" /> {t("preview.exit")}
            </Button>
          </div>
        </div>
      )}
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
        <button
          onClick={goBrowse}
          className="flex items-center gap-2 font-semibold tracking-tight"
        >
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
            <Home className="h-5 w-5" />
          </span>
          <span className="text-lg">{t("app.name")}</span>
        </button>

        <nav className="ml-4 hidden items-center gap-1 md:flex">{nav}</nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          {user && (user.role === "OWNER" || user.role === "ADMIN") && (
            <Button
              size="sm"
              onClick={openNewProduct}
              className="hidden lg:inline-flex"
            >
              {t("nav.listProperty")}
            </Button>
          )}
          <LanguageSwitcher />
          <ThemeToggle />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <UserCircle className="h-4 w-4" />
                  <span className="hidden sm:inline max-w-[120px] truncate">
                    {displayName}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="flex flex-col gap-1">
                    <span>{displayName}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      @{user.username}
                    </span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge
                        variant="outline"
                        className={`w-fit ${roleBadgeClass(user.role)}`}
                      >
                        {roleLabel(user.role, locale)}
                      </Badge>
                      {user.userGroup && (
                        <Badge
                          variant="outline"
                          className={
                            user.userGroup === "FAMILY"
                              ? "w-fit border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "w-fit border-sky-200 bg-sky-50 text-sky-700"
                          }
                        >
                          {t(`group.${user.userGroup}`)}
                        </Badge>
                      )}
                      {!user.validated && user.role === "USER" && (
                        <Badge
                          variant="outline"
                          className="w-fit border-amber-300 bg-amber-50 text-amber-700"
                        >
                          {t("mod.notValidated")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setView("my-reservations")}>
                  <CalendarCheck className="mr-2 h-4 w-4" /> {t("nav.myTripsShort")}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/documentation">
                    <BookOpen className="mr-2 h-4 w-4" /> {t("nav.documentation")}
                  </Link>
                </DropdownMenuItem>
                {(user.role === "OWNER" || user.role === "ADMIN") && (
                  <DropdownMenuItem onClick={() => setView("host")}>
                    <LayoutDashboard className="mr-2 h-4 w-4" /> {t("nav.hostPanelShort")}
                  </DropdownMenuItem>
                )}
                {(isStaffRole(user.role)) && (
                  <>
                    <DropdownMenuItem onClick={() => setView("moderator")}>
                      <ShieldCheck className="mr-2 h-4 w-4" /> {t("nav.moderator")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setView("expenses")}>
                      <Receipt className="mr-2 h-4 w-4" /> {t("nav.expenses")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setView("dashboard")}>
                      <PieChart className="mr-2 h-4 w-4" /> {t("nav.dashboard")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setView("notifications")}>
                      <Bell className="mr-2 h-4 w-4" /> {t("nav.notifications")}
                    </DropdownMenuItem>
                  </>
                )}
                {user.role === "ADMIN" && (
                  <DropdownMenuItem onClick={() => setView("admin")}>
                    <Shield className="mr-2 h-4 w-4" /> {t("nav.adminShort")}
                  </DropdownMenuItem>
                )}
                {user.role === "ADMIN" && (
                  <DropdownMenuItem onClick={() => setPreviewOpen(true)}>
                    <Eye className="mr-2 h-4 w-4" /> {t("preview.as")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> {t("common.signout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openAuth("login")}
                className="hidden sm:inline-flex"
              >
                {t("common.signin")}
              </Button>
              <Button size="sm" onClick={() => openAuth("register")}>
                {t("common.signup")}
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t bg-background md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <NavButton
              active={view === "browse" && !docsActive}
              onClick={() => {
                goBrowse();
                setMobileOpen(false);
              }}
              icon={Home}
            >
              {t("nav.browse")}
            </NavButton>
            {user && (
              <NavButton
                active={view === "my-reservations"}
                onClick={() => {
                  setView("my-reservations");
                  setMobileOpen(false);
                }}
                icon={CalendarCheck}
              >
                {t("nav.myTrips")}
              </NavButton>
            )}
            {user && (user.role === "OWNER" || user.role === "ADMIN") && (
              <>
                <NavButton
                  active={view === "host"}
                  onClick={() => {
                    setView("host");
                    setMobileOpen(false);
                  }}
                  icon={Building2}
                >
                  {t("nav.hostPanel")}
                </NavButton>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    openNewProduct();
                    setMobileOpen(false);
                  }}
                  className="w-fit"
                >
                  {t("nav.listProperty")}
                </Button>
              </>
            )}
            {user && (isStaffRole(user.role)) && (
              <>
                <NavButton
                  active={view === "moderator"}
                  onClick={() => {
                    setView("moderator");
                    setMobileOpen(false);
                  }}
                  icon={ShieldCheck}
                >
                  {t("nav.moderator")}
                </NavButton>
                <NavButton
                  active={view === "expenses"}
                  onClick={() => {
                    setView("expenses");
                    setMobileOpen(false);
                  }}
                  icon={Receipt}
                >
                  {t("nav.expenses")}
                </NavButton>
                <NavButton
                  active={view === "dashboard"}
                  onClick={() => {
                    setView("dashboard");
                    setMobileOpen(false);
                  }}
                  icon={PieChart}
                >
                  {t("nav.dashboard")}
                </NavButton>
                <NavButton
                  active={view === "notifications"}
                  onClick={() => {
                    setView("notifications");
                    setMobileOpen(false);
                  }}
                  icon={Bell}
                >
                  {t("nav.notifications")}
                </NavButton>
              </>
            )}
            {user && user.role === "ADMIN" && (
              <NavButton
                active={view === "admin"}
                onClick={() => {
                  setView("admin");
                  setMobileOpen(false);
                }}
                icon={Shield}
              >
                {t("nav.admin")}
              </NavButton>
            )}
            <Button
              variant="ghost"
              size="sm"
              className={`w-fit gap-2 border-0 ${
                docsActive
                  ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  : "text-foreground hover:bg-muted"
              }`}
              asChild
              onClick={() => setMobileOpen(false)}
            >
              <Link href="/documentation">
                <BookOpen className="h-4 w-4" />
                {t("nav.documentation")}
              </Link>
            </Button>
            {!user && (
              <div className="mt-2 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    openAuth("login");
                    setMobileOpen(false);
                  }}
                >
                  {t("common.signin")}
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    openAuth("register");
                    setMobileOpen(false);
                  }}
                >
                  {t("common.signup")}
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
    <PreviewUsersModal
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
    />
    </>
  );
}
